import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Printer, ShieldCheck, Lock, AlertTriangle, ArrowLeft, Clock, KeyRound, Copy, Check, Ban, GitBranch } from 'lucide-react';
import DOMPurify from 'dompurify';
import supabase from '../supabase/supabase';
import { useData } from '../contexts/DataContext';
import { reportValidationUrl, reportValidationDisplay } from '../utils/site';
import { reportAssetUrl, areReportAssetsReady } from '../utils/reportPayload';

// report.content é HTML gerado pelo editor rico (Quill) e pode ter sido
// manipulado fora da UI (ex.: chamada direta à API). Sem isso, um relato
// malicioso executaria JS no navegador de qualquer paciente/tutor — ou de
// um profissional autenticado — que abrisse o link público do laudo.
// A whitelist reflete exatamente a toolbar do editor (sem imagem/script/iframe).
const sanitizeReportContent = (html) => DOMPurify.sanitize(html || '', {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'ol', 'ul', 'li', 'a', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
});

const ReportView = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const { getReport } = useData();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(tokenFromUrl || '');
  const [isVerifying, setIsVerifying] = useState(!!tokenFromUrl);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [signatureLoaded, setSignatureLoaded] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      const now = new Date();
      const diff = new Date(lockedUntil) - now;
      if (diff <= 0) {
        setLockedUntil(null);
        setError(null);
        clearInterval(interval);
      } else {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const fetchPublicReport = useCallback(async (tokenToUse) => {
    setIsVerifying(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('verify_report_access', {
        p_report_id: id,
        p_token: tokenToUse
      });

      if (rpcError) throw rpcError;

      const result = data[0];
      if (!result.success) {
        if (result.locked_until) setLockedUntil(result.locked_until);
        throw new Error(result.message);
      }
      setImagesLoaded(0);
      setLogoLoaded(false);
      setSignatureLoaded(false);
      setReport(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsVerifying(false);
    }
  }, [id]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await getReport(id);
        if (r && (r.status === 'signed' || r.status === 'revoked')) {
          // Carregou pela RLS => é o dono do laudo (mostramos o painel de gestão).
          // Revogado também entra aqui: o dono precisa ver seu próprio laudo
          // revogado sem precisar digitar o token.
          setIsOwner(true);
          setImagesLoaded(0);
          setLogoLoaded(false);
          setSignatureLoaded(false);
          // Logo e assinatura vivem no bucket privado. O dono usa o mesmo proxy
          // protegido por report_id + token da visualização pública, evitando
          // caminhos privados crus no src da imagem e mantendo o PDF consistente.
          const locationLogoUrl = r.locations?.show_logo && r.locations?.logo_url
            ? reportAssetUrl(r, 'location')
            : null;
          const signatureImageUrl = r.signatures?.image_url
            ? reportAssetUrl(r, 'signature')
            : null;

          // Normaliza o autor para visualização interna
          setReport({
            ...r,
            location_name: r.locations?.show_name === false ? null : r.locations?.name,
            location_logo_url: locationLogoUrl,
            location_address: r.locations?.show_address === false ? null : r.locations?.address,
            location_phone: r.locations?.show_phone === false ? null : r.locations?.phone,
            location_cnpj: r.locations?.show_cnpj === false ? null : r.locations?.cnpj,
            signature_image_url: signatureImageUrl,
            author_profile: {
              // Prioriza o nome do perfil do sistema se disponível, senão nome da assinatura
              name: r.users?.name || r.author_name || r.signatures?.name,
              crm: r.signatures?.crm || r.users?.crm,
              uf: r.signatures?.uf || r.users?.uf,
              type: r.signatures?.type || r.users?.type
            }
          });
        } else if (tokenFromUrl) {
          fetchPublicReport(tokenFromUrl);
        }
      } catch (err) {
        if (tokenFromUrl) fetchPublicReport(tokenFromUrl);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, getReport, tokenFromUrl, fetchPublicReport]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-paper-alt">
      <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" role="status" aria-label="Carregando" />
    </div>
  );

  if (!report) {
    // Sem laudo carregado ainda — cobre tanto o estado ocioso (aguardando
    // digitação) quanto o meio da verificação (isVerifying=true, spinner no
    // botão abaixo). NUNCA cair para o render do documento com report=null:
    // isso já causou tela branca (TypeError em report.author_profile).
    // Página pública de acesso: sóbria, sem navegação de marketing.
    // Erro de verificação usa o triângulo de alerta (uso correto e exclusivo).
    return (
      <div className="min-h-screen bg-paper-alt flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-paper rounded-card shadow-card p-8 text-center border border-line">
          <div className="w-16 h-16 bg-paper-alt text-navy rounded-card flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8" aria-hidden="true" />
          </div>
          <h2 className="font-display text-xl font-bold text-navy mb-2">Documento protegido</h2>
          <p className="text-sm text-muted mb-8">Digite o código de 6 dígitos impresso no laudo para visualizar este documento.</p>
          <div className="space-y-4">
            <label htmlFor="access-token" className="sr-only">Código de acesso de 6 dígitos</label>
            <input
              id="access-token"
              type="text"
              maxLength={6}
              value={token}
              onChange={e => setToken(e.target.value.toUpperCase())}
              placeholder="000000"
              disabled={!!lockedUntil}
              className="w-full px-6 py-4 bg-paper-alt border border-line rounded-card text-center text-2xl font-mono font-bold tracking-[0.5em] focus:ring-2 focus:ring-navy/15 focus:border-navy outline-none transition-colors disabled:opacity-50"
            />
            {error && (
              <div className="flex items-start justify-center gap-2 text-danger text-xs font-semibold bg-danger-tint py-3 px-4 rounded-card border border-danger/25 text-left">
                {lockedUntil
                  ? <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />
                  : <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />}
                <span>
                  {lockedUntil
                    ? `Muitas tentativas. Aguarde ${timeLeft} e tente novamente.`
                    : `${error} Confira o código no documento impresso ou peça um novo link a quem emitiu o laudo.`}
                </span>
              </div>
            )}
            <button
              onClick={() => fetchPublicReport(token)}
              disabled={token.length !== 6 || isVerifying || !!lockedUntil}
              className="btn-primary w-full"
            >
              {isVerifying ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" role="status" aria-label="Verificando" /> : 'Verificar e acessar'}
            </button>
          </div>
        </div>
        <p className="mt-6 text-xs text-muted">Emitido via <span className="font-semibold">Laudando Online</span></p>
      </div>
    );
  }

  const allAssetsLoaded = areReportAssetsReady(report, { imagesLoaded, logoLoaded, signatureLoaded });
  const isVet = report?.author_profile?.type === 'veterinario';

  return (
    <div className="min-h-screen bg-paper-alt py-8 px-4 print:p-0 print:bg-white report-view-container">
      <style>{`
        @media print {
          @page {
            margin: 25mm 15mm;
          }
          body {
            -webkit-print-color-adjust: exact;
          }
          .report-view-container {
            padding: 0 !important;
          }
        }
      `}</style>

      <div className="max-w-[210mm] mx-auto mb-6 flex justify-between items-center print:hidden">
        <Link to="/app/meus-laudos" className="flex items-center gap-2 min-h-[44px] text-muted hover:text-ink font-semibold text-xs uppercase tracking-widest transition-colors">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Voltar
        </Link>
        <button
          onClick={handlePrint}
          disabled={!allAssetsLoaded}
          className={allAssetsLoaded ? 'btn-primary' : 'btn-secondary opacity-70 cursor-not-allowed'}
        >
          {allAssetsLoaded
            ? <Printer className="w-4 h-4" aria-hidden="true" />
            : <Clock className="w-4 h-4 animate-spin" aria-hidden="true" />}
          {allAssetsLoaded ? 'Imprimir / Salvar PDF' : 'Carregando imagens do documento'}
        </button>
      </div>

      {/* Aviso de revogação: inequívoco, mas o conteúdo abaixo continua legível.
          Revogação é terminal e distinta de "substituído por versão nova". */}
      {report.status === 'revoked' && (
        <div className="max-w-[210mm] mx-auto mb-6 bg-danger text-white rounded-card shadow-card p-5 flex items-start gap-3">
          <Ban className="w-6 h-6 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h2 className="font-display text-base font-bold mb-1">Este documento foi revogado</h2>
            <p className="text-sm text-white/85">
              {report.revoked_at && `Revogado em ${new Date(report.revoked_at).toLocaleDateString('pt-BR')}. `}
              {report.revocation_reason
                ? <>Motivo: {report.revocation_reason}</>
                : 'Este laudo não deve mais ser considerado válido.'}
              {' '}O conteúdo abaixo é mantido para consulta, mas não representa mais um laudo em vigor.
            </p>
          </div>
        </div>
      )}

      {/* Substituído por uma versão mais nova, distinto de revogação: o conteúdo
          seguia válido até ser corrigido/atualizado pelo profissional. */}
      {report.status !== 'revoked' && report.superseded_at && (
        <div className="max-w-[210mm] mx-auto mb-6 bg-paper-alt border border-line rounded-card shadow-card p-5 flex items-start gap-3">
          <GitBranch className="w-5 h-5 text-navy shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h2 className="text-sm font-semibold text-ink mb-1">Uma versão mais recente deste laudo existe</h2>
            <p className="text-sm text-muted">
              Esta versão foi substituída em {new Date(report.superseded_at).toLocaleDateString('pt-BR')}.
              Fale com o profissional que emitiu o laudo para obter o link da versão atual.
            </p>
          </div>
        </div>
      )}

      {/* Painel do dono: senha (token) + link de validação para repassar ao paciente.
          Visível só para quem emitiu (carregou pela RLS), nunca na versão impressa. */}
      {isOwner && report.access_token && (() => {
        const link = reportValidationUrl(report.id);
        const copy = (val, key) => {
          navigator.clipboard?.writeText(val).then(() => { setCopied(key); setTimeout(() => setCopied(''), 1500); }).catch(() => {});
        };
        return (
          <div className="max-w-[210mm] mx-auto mb-6 bg-paper border border-line rounded-card shadow-card p-5 print:hidden">
            <h2 className="mb-3 text-sm font-semibold text-ink">Compartilhar com o paciente</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-1">Senha de acesso</p>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-lg text-ink tracking-[0.3em]">{report.access_token}</span>
                  <button onClick={() => copy(report.access_token, 'token')} className="text-navy hover:text-navy-2" title="Copiar senha" aria-label="Copiar senha">
                    {copied === 'token' ? <Check className="w-4 h-4 text-selo" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-1">Link de validação</p>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-sm text-navy truncate">{link}</span>
                  <button onClick={() => copy(link, 'link')} className="text-navy hover:text-navy-2 shrink-0" title="Copiar link" aria-label="Copiar link">
                    {copied === 'link' ? <Check className="w-4 h-4 text-selo" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted mt-3">
              O paciente abre o link e digita a senha para validar. A senha também aparece impressa no rodapé do laudo.
            </p>
          </div>
        );
      })()}

      {/* Documento: estrutura do Certificate Card em escala A4 */}
      <div className="max-w-[210mm] mx-auto bg-white shadow-card border border-line print:border-0 print:shadow-none print:w-full min-h-[297mm] flex flex-col overflow-hidden">
        {/* Faixa navy com selo de verificação; nunca diz "verificado" para um laudo revogado */}
        <div className="bg-navy px-10 py-3 flex items-center justify-between print:hidden">
          <span className="text-white text-xs font-semibold uppercase tracking-widest">
            {report.status === 'revoked' ? 'Laudo revogado' : 'Laudo registrado'}
          </span>
          {report.status === 'revoked' ? (
            <span className="inline-flex items-center gap-1.5 bg-danger text-white text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-md">
              <Ban className="w-3.5 h-3.5" aria-hidden="true" /> Revogado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 bg-selo text-white text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-md">
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Documento verificado
            </span>
          )}
        </div>

        {/* Cabeçalho */}
        <div className="px-10 py-6 border-b-2 border-line flex items-center justify-between gap-8">
          <div className="flex-1">
            {report.location_name && <h1 className="font-display text-xl font-bold text-ink mb-1">{report.location_name}</h1>}
            <div className="text-[10px] text-muted space-y-0 leading-tight">
              {report.location_address && <p>{report.location_address}</p>}
              <div className="flex gap-4">
                {report.location_phone && <p>Tel: {report.location_phone}</p>}
                {report.location_cnpj && <p className="font-mono">CNPJ: {report.location_cnpj}</p>}
              </div>
            </div>
          </div>
          {report.location_logo_url && (
            <img
              src={report.location_logo_url}
              alt={`Logo de ${report.location_name || 'local de atendimento'}`}
              onLoad={() => setLogoLoaded(true)}
              className="h-14 w-auto object-contain"
            />
          )}
        </div>

        {/* Info Paciente */}
        <div className="px-10 py-5 bg-paper-alt/50 grid grid-cols-2 md:grid-cols-3 gap-y-2.5 gap-x-8 border-b border-line">
          <div>
            <span className="text-[9px] font-semibold text-muted uppercase tracking-[0.15em] mb-0.5 block">Paciente</span>
            <p className="text-xs font-semibold text-ink">{report.patient_name || 'Não informado'}</p>
          </div>
          <div>
            <span className="text-[9px] font-semibold text-muted uppercase tracking-[0.15em] mb-0.5 block">Responsável</span>
            <p className="text-xs font-semibold text-ink">{report.patient_owner || 'Não informado'}</p>
          </div>
          <div>
            <span className="text-[9px] font-semibold text-muted uppercase tracking-[0.15em] mb-0.5 block">Data do exame</span>
            <p className="text-xs font-semibold text-ink font-mono">{report.exam_date || 'Não informado'}</p>
          </div>
          <div>
            <span className="text-[9px] font-semibold text-muted uppercase tracking-[0.15em] mb-0.5 block">Espécie / Raça</span>
            <p className="text-xs font-semibold text-ink">
              {report.patient_species || '-'} {report.patient_breed ? `/ ${report.patient_breed}` : ''}
            </p>
          </div>
          <div>
            <span className="text-[9px] font-semibold text-muted uppercase tracking-[0.15em] mb-0.5 block">Idade / Sexo</span>
            <p className="text-xs font-semibold text-ink">
              {report.patient_age || '-'} {report.patient_sex ? `/ ${report.patient_sex}` : ''}
            </p>
          </div>
          <div>
            <span className="text-[9px] font-semibold text-muted uppercase tracking-[0.15em] mb-0.5 block">Solicitante</span>
            <p className="text-xs font-semibold text-ink">{report.requesting_professional || 'Não informado'}</p>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="px-10 py-8 flex-1">
          {report.request_reason && (
            <div className="mb-6 p-3 bg-paper-alt rounded-card border-l-4 border-line">
              <span className="text-[9px] font-semibold text-muted uppercase tracking-widest mb-0.5 block">Motivo do exame</span>
              <p className="text-[11px] text-muted italic leading-snug text-justify">{report.request_reason}</p>
            </div>
          )}

          <div
            className="prose prose-slate max-w-none text-ink leading-relaxed text-sm text-justify report-content"
            dangerouslySetInnerHTML={{ __html: sanitizeReportContent(report.content) }}
          />

          {/* Galeria */}
          {report.images && report.images.length > 0 && (
            <div className="mt-12 pt-8 border-t border-line">
              <h3 className="text-[10px] font-semibold text-muted uppercase tracking-[0.2em] mb-6 text-center">Anexos fotográficos</h3>
              <div className="grid grid-cols-2 gap-6 items-start">
                {report.images.map((url, idx) => (
                  <div key={idx} className="flex flex-col items-center break-inside-avoid py-2">
                    <div className="w-full bg-paper-alt rounded-card border border-line overflow-hidden">
                      <img
                        src={url}
                        alt={`Anexo ${idx + 1}`}
                        onLoad={() => setImagesLoaded(prev => prev + 1)}
                        className="w-full h-auto block"
                      />
                    </div>
                    <span className="mt-2 text-[9px] font-semibold text-muted uppercase tracking-widest">Figura {idx + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Rodapé de autenticidade */}
        <div className="p-10 bg-paper-alt/40 border-t border-line break-inside-avoid">
          <div className="flex flex-col items-center text-center">
            {report.signature_image_url ? (
              <img
                src={report.signature_image_url}
                alt="Assinatura do responsável"
                onLoad={() => setSignatureLoaded(true)}
                className="h-16 w-auto mb-2 object-contain"
              />
            ) : (
              <div className="h-16 flex items-end mb-2"><div className="w-48 border-b-2 border-line" /></div>
            )}

            {report.author_profile?.name && !report.author_profile.name.toLowerCase().includes('assinatura principal') && (
              <p className="text-sm font-bold text-ink">
                {report.author_profile.name}
              </p>
            )}

            {(report.author_profile?.crm || report.author_profile?.uf) && (
              <p className="text-[10px] text-muted font-mono mt-0.5">
                {isVet ? 'CRMV' : 'CRM'} {report.author_profile.crm || ''} {report.author_profile.uf ? `- ${report.author_profile.uf}` : ''}
              </p>
            )}
          </div>

          <div className="mt-10 flex justify-between items-end border-t border-line pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-selo-tint text-selo rounded-card flex items-center justify-center">
                <ShieldCheck className="w-6 h-6" aria-hidden="true" />
              </div>
              <div className="text-left">
                <p className="text-[9px] font-semibold text-muted uppercase tracking-widest">Documento original</p>
                <p className="text-[9px] font-mono text-muted leading-tight">SHA-256: {report.hash?.substring(0, 24)}…</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[9px] font-semibold text-muted uppercase tracking-widest">Validação</p>
              <p className="text-[10px] font-mono font-medium text-navy">{reportValidationDisplay(report.id)}</p>
              <p className="text-[9px] font-mono text-muted mt-1">Código: {report.access_token}</p>
            </div>
          </div>

          <p className="mt-6 text-center text-[9px] text-muted print:block">Emitido via Laudando Online</p>
        </div>
      </div>
    </div>
  );
};

export default ReportView;
